import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../Config";
import Spinner from "react-bootstrap/Spinner";
import moment from "moment";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";

/*
 * Printable Vehicle Profit & Loss. Carries the same filters the screen was showing, so
 * the printout and the screen cannot disagree.
 */
function PrintVehiclePL() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const MemberType = localStorage.getItem("MemberType");

  const urlParams = new URLSearchParams(window.location.search);
  const fromMonth = urlParams.get("from") || moment().startOf("year").format("YYYY-MM");
  const toMonth = urlParams.get("to") || moment().format("YYYY-MM");
  const vehicle = urlParams.get("vehicle") || "";
  const groupByBus = urlParams.get("group") !== "0";

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    document.title = "Vehicle Profit & Loss Report";

    const f = moment(fromMonth, "YYYY-MM");
    const t = moment(toMonth, "YYYY-MM");

    axios({
      method: "post",
      url: config.base_url + "TransportReport/GetVehiclePL",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType: MemberType || "Admin",
        SchoolID: 1,
        Vehicle_ID: vehicle ? parseInt(vehicle, 10) : 0,
        From_Year: f.year(),
        From_Month: f.month() + 1,
        To_Year: t.year(),
        To_Month: t.month() + 1,
      },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setRows(res.data.ListofRecords || []);
          setSummary(res.data.Summary || null);
        }
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, vehicle, fromMonth, toMonth]);

  const money = (n) =>
    n === null || n === undefined || n === 0
      ? "-"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const byBus = () => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.Vehicle_ID)) {
        map.set(r.Vehicle_ID, {
          Registration_No: r.Registration_No,
          Model: r.Model,
          Students: 0,
          Billed: 0,
          Received: 0,
          Outstanding: 0,
          FuelCost: 0,
          MaintCost: 0,
          PartsCost: 0,
          TotalCost: 0,
        });
      }
      const a = map.get(r.Vehicle_ID);
      a.Students = Math.max(a.Students, r.Students);
      a.Billed += r.Billed;
      a.Received += r.Received;
      a.Outstanding += r.Outstanding;
      a.FuelCost += r.FuelCost;
      a.MaintCost += r.MaintCost;
      a.PartsCost += r.PartsCost;
      a.TotalCost += r.TotalCost;
    });
    return Array.from(map.values()).map((a) => ({ ...a, Profit: a.Received - a.TotalCost }));
  };

  const display = groupByBus ? byBus() : rows;
  const totalOf = (f) => display.reduce((s, r) => s + (r[f] || 0), 0);

  return (
    <>
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <Page orientation="landscape" size="A4">
          <PrintHeader title="Vehicle Profit & Loss Report" />

          <div style={{ fontSize: "10pt", marginBottom: "8px" }}>
            <b>Period:</b> {moment(fromMonth, "YYYY-MM").format("MMM YYYY")} to{" "}
            {moment(toMonth, "YYYY-MM").format("MMM YYYY")}
            {summary ? (
              <>
                {"   |   "}
                <b>Revenue:</b> Rs. {money(summary.FleetRevenue)}
                {"   |   "}
                <b>Expense:</b> Rs. {money(summary.FleetExpense)}
                {"   |   "}
                <b>Profit:</b> Rs. {money(summary.FleetProfit)}
                {"   |   "}
                <b>Unpaid:</b> Rs. {money(summary.FleetOutstanding)}
              </>
            ) : null}
          </div>

          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Bus</th>
                <th>{groupByBus ? "Model" : "Month"}</th>
                <th style={{ textAlign: "right" }}>Students</th>
                <th style={{ textAlign: "right" }}>Billed</th>
                <th style={{ textAlign: "right" }}>Received</th>
                <th style={{ textAlign: "right" }}>Unpaid</th>
                <th style={{ textAlign: "right" }}>Fuel</th>
                <th style={{ textAlign: "right" }}>Maint.</th>
                <th style={{ textAlign: "right" }}>Parts</th>
                <th style={{ textAlign: "right" }}>Total cost</th>
                <th style={{ textAlign: "right" }}>Profit</th>
              </tr>
            </thead>
            <tbody>
              {display.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: "center" }}>
                    Nothing posted in this period
                  </td>
                </tr>
              ) : (
                display.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{r.Registration_No}</td>
                    <td>{groupByBus ? r.Model || "-" : `${r.MonthName} ${r.Yr}`}</td>
                    <td style={{ textAlign: "right" }}>{r.Students}</td>
                    <td style={{ textAlign: "right" }}>{money(r.Billed)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.Received)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.Outstanding)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.FuelCost)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.MaintCost)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.PartsCost)}</td>
                    <td style={{ textAlign: "right" }}>{money(r.TotalCost)}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {money(groupByBus ? r.Profit : r.ProfitOnReceived)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {display.length > 0 ? (
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td colSpan="4" style={{ textAlign: "right" }}>
                    TOTALS
                  </td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("Billed"))}</td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("Received"))}</td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("Outstanding"))}</td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("FuelCost"))}</td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("MaintCost"))}</td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("PartsCost"))}</td>
                  <td style={{ textAlign: "right" }}>{money(totalOf("TotalCost"))}</td>
                  <td style={{ textAlign: "right" }}>
                    {money(totalOf(groupByBus ? "Profit" : "ProfitOnReceived"))}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </Page>
      )}
    </>
  );
}

export default PrintVehiclePL;
